import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useImages } from "@/context/SiteConfigContext";
import { MaskWords, Pill } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";
import { scrollToId } from "@/hooks/useSmoothScroll";

export default function About() {
  const { t } = useLang();
  const a = t.about;
  const images = useImages();

  const gallery = (
    Array.isArray(images.about_gallery) && images.about_gallery.filter(Boolean).length
      ? images.about_gallery
      : [images.about_gallery_1, images.about_gallery_2, images.about_gallery_3, images.about_gallery_4]
  ).filter(Boolean);
  const [lightbox, setLightbox] = useState(null); // active index | null
  const stripRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: true });

  const updateEdges = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const padL = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    setEdges({
      left: el.scrollLeft > padL + 8,
      right: el.scrollLeft < el.scrollWidth - el.clientWidth - 8,
    });
  }, []);

  const scrollStrip = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const close = useCallback(() => setLightbox(null), []);
  const go = useCallback(
    (dir) => setLightbox((i) => (i === null ? i : (i + dir + gallery.length) % gallery.length)),
    [gallery.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, go]);

  // Desktop: translate vertical wheel into fluid horizontal scroll on the gallery strip.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => {
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", updateEdges);
    };
  }, [gallery.length, updateEdges]);

  return (
    <div className="bg-paper text-ink min-h-screen">
      {/* Header + portrait */}
      <section className="px-6 md:px-12 lg:px-20 pt-36 md:pt-48 pb-16 md:pb-24">
        <div className="eyebrow text-ink/50 mb-6">{a.label}</div>
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <MaskWords Tag="h1" text={a.title} className="display uppercase text-[13vw] md:text-[12vw] lg:text-[10vw] leading-[0.82] break-words" />
            <p className="serif italic text-2xl md:text-4xl leading-[1.15] text-ink/75 mt-8 max-w-2xl">{a.lede}</p>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <div className="overflow-hidden aspect-[3/4]">
                <img src={images.about_portrait} alt="Oana Mudura" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Bio */}
      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          <div className="col-span-12 lg:col-span-8 lg:col-start-5 space-y-7">
            {a.bio.map((p, i) => (
              <Reveal key={i}>
                <p className={`text-xl md:text-2xl leading-[1.6] text-ink/85 ${i === 0 ? "font-medium" : ""}`}>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Career-chapter gallery — horizontal feed (swipe on mobile, fluid scroll on desktop) */}
      {gallery.length > 0 && (
        <section className="py-24 md:py-40 relative" data-testid="about-gallery">
          <div
            ref={stripRef}
            onScroll={updateEdges}
            className="flex gap-4 md:gap-6 overflow-x-auto px-6 md:px-12 lg:px-20 pb-5 snap-x snap-mandatory scroll-smooth"
          >
            {gallery.map((src, i) => (
              <button
                type="button"
                key={i}
                onClick={() => setLightbox(i)}
                data-cursor="link"
                data-testid={`about-gallery-img-${i}`}
                className="group shrink-0 snap-start h-[300px] md:h-[400px] overflow-hidden bg-ink/5"
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="h-full w-auto max-w-none object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => scrollStrip(-1)}
            aria-label="Glisează la stânga"
            data-testid="about-gallery-prev"
            className={`hidden md:flex absolute top-1/2 -translate-y-1/2 left-4 lg:left-6 z-10 w-11 h-11 items-center justify-center rounded-full border border-ink/25 bg-paper/70 backdrop-blur-sm text-ink hover:bg-ink hover:text-paper transition-all duration-300 ${edges.left ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => scrollStrip(1)}
            aria-label="Glisează la dreapta"
            data-testid="about-gallery-next"
            className={`hidden md:flex absolute top-1/2 -translate-y-1/2 right-4 lg:right-6 z-10 w-11 h-11 items-center justify-center rounded-full border border-ink/25 bg-paper/70 backdrop-blur-sm text-ink hover:bg-ink hover:text-paper transition-all duration-300 ${edges.right ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        </section>
      )}

      {/* Timeline */}
      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-36">
        <div className="eyebrow text-ink/45 mb-10">{a.timeline.label}</div>
        <div className="border-t border-ink/15">
          {a.timeline.entries.map((e, i) => (
            <Reveal key={i}>
              <div className="grid grid-cols-12 gap-4 md:gap-8 py-8 border-b border-ink/15 group">
                <div className="col-span-12 md:col-span-3 eyebrow text-ink/50 tnum">{e.years}</div>
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display text-2xl md:text-3xl leading-tight transition-transform duration-500 group-hover:translate-x-1">{e.role}</div>
                  <div className="eyebrow text-ink/45 mt-1">{e.org}</div>
                </div>
                <p className="col-span-12 md:col-span-4 text-ink/65 leading-[1.55]">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Education + Appearances */}
      <section className="px-6 md:px-12 lg:px-20 pb-28 md:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="eyebrow text-ink/45 mb-8">{a.education.label}</div>
            <div className="space-y-6">
              {a.education.items.map((e, i) => (
                <div key={i} className="border-b border-ink/12 pb-5">
                  <div className="font-display text-2xl">{e.title}</div>
                  <div className="eyebrow text-ink/45 mt-1">{e.org}{e.years ? ` · ${e.years}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow text-ink/45 mb-8">{a.appearances.label}</div>
            <div className="space-y-6">
              {a.appearances.items.map((e, i) => (
                <div key={i} className="flex items-baseline justify-between border-b border-ink/12 pb-5">
                  <span className="font-display text-2xl">{e.name}</span>
                  <span className="eyebrow text-ink/45">{e.place}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Reveal>
          <div className="mt-20">
            <Pill onClick={() => scrollToId("#contact")}>{t.contact.big}</Pill>
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="about-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 md:p-16"
          >
            <button
              type="button"
              onClick={close}
              data-testid="about-lightbox-close"
              aria-label="Închide"
              className="absolute top-6 right-6 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors"
            >
              <X size={28} strokeWidth={1.5} />
            </button>
            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); go(-1); }}
                  data-testid="about-lightbox-prev"
                  aria-label="Anterior"
                  className="absolute left-3 md:left-10 text-white/55 hover:text-white transition-colors"
                >
                  <ChevronLeft size={40} strokeWidth={1} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); go(1); }}
                  data-testid="about-lightbox-next"
                  aria-label="Următor"
                  className="absolute right-3 md:right-10 text-white/55 hover:text-white transition-colors"
                >
                  <ChevronRight size={40} strokeWidth={1} />
                </button>
              </>
            )}
            <motion.img
              key={lightbox}
              src={gallery[lightbox]}
              alt=""
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
